import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Notification, Spinner } from '@xsolla/ui-core';

import { EmailProvider, getEmailProvider, putEmailProvider } from '../../../api';
import { Error } from '../../../api/setup/getApiError';
import { useConfig } from '../../../contexts/config';
import { useCurrentProject } from '../../../contexts/settings';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { HorizontalCenterBlock, ShortSettingsBlock } from '../../../styled';
import { linkToUnderline } from '../../../utils';
import {
  CardGroup,
  FormFooter,
  HeaderTitle,
  Notice,
  SendGridIcon,
  Separator,
  SMTPIcon,
  ToNavigationError
} from '../../ui';
import { FieldWrapper, SettingsWrapper } from '../style';
import { EmailFields } from './EmailFields';
import { SendGridFields } from './SendGridFields';
import { SMTPFields } from './SMTPFields';
import {
  EmailProviders,
  FormValues,
  ProviderFieldsProps,
  ValidationError,
  ValidationErrorObject
} from './types';
import { prepareSubmitData } from './utils';
import { EmailProvidersValidator } from './validator';

const EmailProviderFields = {
  [EmailProviders.send_grid]: (props: ProviderFieldsProps) => <SendGridFields {...props} />,
  [EmailProviders.smtp]: (props: ProviderFieldsProps) => <SMTPFields {...props} />
};

const defaultState: EmailProvider = {
  custom_email: '',
  from_email_name: '',
  provider_settings: {
    sendgrid_api_key: ''
  }
};

const CustomEmailProvider = ({ history, match }: RouteComponentProps): JSX.Element => {
  const { emailCustomizationPath, navigationPath } = usePaths();
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;
  const { currentProject } = useCurrentProject();
  const { xsollaAnalytics } = useConfig();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const [emailData, setEmailData] = useState<EmailProvider>(defaultState);
  const [fetchingData, setFetchingData] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [errorData, setErrorData] = useState<Error | null>(null);

  useEffect(() => {
    xsollaAnalytics.sendOpen('login_pa_module__email_provider', 'open');
  }, []);

  const defaultProvider = useMemo(() => {
    // ToDo: change, when SMTP implemented
    // if (emailData.provider_settings.sendgrid_api_key) {
    //   return {
    //     [EmailProviders.send_grid]: true
    //   };
    // }

    return {
      [EmailProviders.send_grid]: true
    };
  }, [emailData]);

  useEffect(() => {
    if (currentProject?.id) {
      getEmailProvider(currentProject.id)
        .then((res) => {
          setEmailData(res);
        })
        .catch((err) => {
          setErrorData(err);
          showErrorNotify(err);
        })
        .finally(() => setFetchingData(false));
    }
  }, [currentProject?.id]);

  const backToNavigation = () =>
    currentProject && history.push({ pathname: navigationPath(currentProject.id) });

  const submitHandle = async (values: FormValues) => {
    try {
      if (currentProject?.id) {
        setFetching(true);
        xsollaAnalytics.sendClick('login_pa_module__email_provider', 'submit', {
          provider_type: values.provider_type
        });

        await putEmailProvider(currentProject.id, prepareSubmitData(values));

        showSuccessNotify();
        setFetching(false);
      }
    } catch (e) {
      showErrorNotify(e);
    }
  };

  const options = [
    {
      name: EmailProviders.send_grid,
      label: intl.formatMessage({ id: 'custom-providers.settings.email.provider.send-grid' }),
      icon: <SendGridIcon />,
      disabled: true
    },
    {
      name: EmailProviders.smtp,
      label: intl.formatMessage({ id: 'custom-providers.settings.email.provider.smtp' }),
      icon: <SMTPIcon />,
      disabled: true
    }
  ];

  if (fetchingData) {
    return (
      <HorizontalCenterBlock>
        <Spinner />
      </HorizontalCenterBlock>
    );
  }

  if (errorData) {
    return <ToNavigationError error={errorData} pageName="custom-email-provider_open" />;
  }

  return (
    <Form
      onSubmit={submitHandle}
      initialValues={{
        custom_email: emailData.custom_email,
        from_email_name: emailData.from_email_name,
        provider_settings: { ...emailData.provider_settings },
        provider_type: defaultProvider
      }}
      validate={EmailProvidersValidator}
      keepDirtyOnReinitialize>
      {({ handleSubmit, values: { provider_type }, errors, pristine }) => {
        return (
          <>
            <HeaderTitle
              title={intl.formatMessage({ id: 'custom-providers.settings.email.title' })}
              subtitle={intl.formatMessage({ id: 'custom-providers.settings.email.subtitle' })}
              notification={intl.formatMessage(
                {
                  id: 'custom-providers.settings.email.notification'
                },
                linkToUnderline({ href: emailCustomizationPath(projectId) })
              )}
              notificationIcon={Notice}
            />

            <HorizontalCenterBlock>
              <ShortSettingsBlock>
                <EmailFields
                  projectName={currentProject?.name}
                  errors={errors as ValidationError}
                />
              </ShortSettingsBlock>
            </HorizontalCenterBlock>

            <Separator />

            <HorizontalCenterBlock>
              <ShortSettingsBlock>
                <SettingsWrapper>
                  <Notification icon={Notice}>
                    {intl.formatMessage({
                      id: 'custom-providers.settings.email.provider.notification'
                    })}
                  </Notification>
                </SettingsWrapper>

                <FieldWrapper>
                  <CardGroup
                    name="provider_type"
                    options={options}
                    defaultValue={defaultProvider}
                  />
                </FieldWrapper>

                {Object.entries(provider_type).map(([key, value]) => {
                  return (
                    value &&
                    EmailProviderFields[key as EmailProviders]?.({
                      errors: (errors || {}) as ValidationErrorObject
                    })
                  );
                })}
              </ShortSettingsBlock>
            </HorizontalCenterBlock>

            {/* ToDo add errors handlers */}
            <FormFooter
              onSubmit={handleSubmit}
              error={false}
              disabled={pristine}
              onCancel={backToNavigation}
              fetching={fetching}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(CustomEmailProvider);
